import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ListDataService } from '../services/list-data.service';
import { FormStatus, LocationType, UserRoleEnum } from '../models/Enums';
import { UserItemForEmailQuestion } from '../models/UserItemForEmailQuestion';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ScheduleDataService } from '../services/schedule-data.service';
import { EmailQuestionFormParameters } from '../models/EmailQuestionFormParameters';
import { Dispatch } from '../models/Dispatch';
import { forkJoin, of } from 'rxjs';
import { ScheduleItem } from '../models/ScheduleItem';
import { ScheduleRequest } from '../models/ScheduleRequest';
import { MessageService } from '../services/message.service';
import { UserDataService } from '../services/user-data.service';

@Component({
    selector: 'app-email-question-form',
    templateUrl: 'email-question-form.component.html',
    styles: [`
      .table-striped > tbody > tr:nth-of-type(odd) > td {
        background-color: #ecf6ff;
      }

      .table-striped > tbody > tr.selected-row > td {
        background-color: #d2ddff !important;
      }
    `],
    standalone: false
})
export class EmailQuestionFormComponent implements OnInit, OnChanges {
    emailForm: FormGroup;
    users: UserItemForEmailQuestion[] = [];
    searchedUsers: UserItemForEmailQuestion[] = [];
    searchTerm: string = '';
    errorMessage: any;
    roles = [];
    formStatus: FormStatus;
    schedule: ScheduleItem;
    dispatch: Dispatch;
    scheduleLocationTypeIsOrigin: boolean = false;
    dispatchLocationTypeIsOrigin: boolean = true;
    @ViewChild("closeButton") closeButton;
    @Input() dispatchId: number;
    @Input() scheduleId: number;
    @Input() emailQuestionFormId: string = 'emailQuestionModal';
    @Input() emailQuestionParameters = new EmailQuestionFormParameters();
    currentUserEmail: string;
    response: { message: string; status: string; };
    constructor(private _listDataService: ListDataService,
        private _userDataService: UserDataService,
        private _scheduleDataService: ScheduleDataService,
        private _messageService: MessageService,
        private fb: FormBuilder) {
    }

    ngOnInit() {
        this.roles.push({ id: "1", roleName: UserRoleEnum.MA, isSelected: true });
        this.roles.push({ id: "2", roleName: UserRoleEnum.Trader, isSelected: true });
        this.roles.push({ id: "3", roleName: UserRoleEnum.Manager, isSelected: true });
        this.roles.push({ id: "4", roleName: UserRoleEnum.IT, isSelected: true });
    }

    private resetComponent() {
        this.emailForm = this.fb.group({
            emailTo: ['', Validators.required],
            emailCc: ['', Validators.required],
            emailSubject: ['', Validators.required],
            emailQuestion: ['', Validators.required],
            emailClosing: ['', Validators.required]
        });
        this.searchTerm = "";
        this.roles.forEach(role => role.isSelected = true);
        this.getAllUsersForSelectedRoles();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.resetComponent();

        this._userDataService.getCurrentUser().subscribe({
            next: (data) => {
                this.currentUserEmail = data.userEmail;
                this.emailForm.patchValue({ emailCc: this.currentUserEmail });
                this.emailForm.patchValue({ emailClosing: "Thanks, \n" + data.userName });
            }
        });

        if (changes["emailQuestionParameters"]) {
            this.formStatus = FormStatus.Normal;
            this.schedule = new ScheduleItem();
            this.dispatch = new Dispatch();
            const scheduleRequest = new ScheduleRequest();
            scheduleRequest.scheduleId = this.emailQuestionParameters.scheduleId;
            const schedule$ = scheduleRequest.scheduleId > 0 ? this._scheduleDataService.getScheduleItem(scheduleRequest) : of(null);
            const dispatch$ = this.emailQuestionParameters.dispatchId > 0 ? this._scheduleDataService.getDispatch(this.emailQuestionParameters.dispatchId) : of(null);

            forkJoin([schedule$, dispatch$]).subscribe(
                ([schedule, dispatch]) => {
                    this.schedule = schedule;
                    this.dispatch = dispatch;
                    this.dispatchLocationTypeIsOrigin = this.emailQuestionParameters.locationType == LocationType.Destination;
                    this.scheduleLocationTypeIsOrigin = this.emailQuestionParameters.locationType == LocationType.Origin;
                    if (schedule != null) {
                        this.setScheduleAndDispatchDetails(dispatch, schedule);
                    }
                    else if (dispatch?.scheduleId > 0) {
                        const dispatchSchedule = new ScheduleRequest();
                        dispatchSchedule.scheduleId = dispatch.scheduleId;
                        this._scheduleDataService.getScheduleItem(dispatchSchedule).subscribe((schedule) => {
                            this.schedule = schedule;
                            this.setScheduleAndDispatchDetails(dispatch, schedule);
                        });
                    }
                });
        }

        this.users.filter(x => x.isSelected == true).forEach(element => {
            element.isSelected = false;
        });

        this.response = { message: "", status: "" };

    }

    private setScheduleAndDispatchDetails(dispatch: Dispatch, schedule: ScheduleItem) {

        this.emailForm.patchValue({ emailTo: dispatch ? dispatch.enteredByEmail : schedule.enteredByEmail });

        const week = dispatch ? `Period: ${this.convertDateString(dispatch?.dispatchBeginDate)} to ${this.convertDateString(dispatch?.dispatchEndDate)}`
            : `Period: ${schedule?.scheduleBeginDate} to ${schedule?.scheduleEndDate}`;
        const commodity = schedule?.commodityCode;
        const locationText = dispatch ? (" Dispatch from " + dispatch.location.city + " to " + schedule.locationCity) : (" Schedule to " + schedule.locationCity);
        const subject = `Scout Question - ${week} - ${commodity} ${locationText}`;
        this.emailForm.patchValue({ emailSubject: subject });
    }

    getAllUsersForSelectedRoles() {
        const roles = this.roles.filter(x => x.isSelected == true).map(x => x.id);
        this._userDataService.getAllUsersFromSelectedRoles(roles).subscribe({
            next: (data) => {
                data.forEach(element => {
                    element.isSelected = false;
                });
                this.users = data;
                this.searchedUsers = data;
            },
            error: (err) => (this.errorMessage = err),
        });
    }

    searchByUser(term: string) {
        this.searchTerm = term.toLowerCase();
        this.searchedUsers = this.users.filter(item =>
            item.userName?.toLocaleLowerCase().includes(this.searchTerm))
    }

    selectRoles(event: Event, index) {
        const isChecked = (event.target as HTMLInputElement).checked;
        let selectedRole = this.roles.find(x => x.id == index);
        selectedRole.isSelected = isChecked;
        this.getAllUsersForSelectedRoles();
    }

    selectUsers(event: Event, user: UserItemForEmailQuestion) {
        const isChecked = (event.target as HTMLInputElement).checked;
        user.isSelected = isChecked;
    }

    sendMail() {
        if (this.emailForm.valid) {
            const formData = this.emailForm.value;
            this._messageService.sendQuestionMail(formData, this.schedule, this.dispatch).subscribe({
                next: (data) => {
                    if (data) {
                        this.response = {
                            message: "Email Sent Successfully",
                            status: "Success"
                        };
                        setTimeout(() => {
                            this.closeButton.nativeElement.click();
                        }, 2000);
                    } else {
                        this.response = {
                            message: "Email sending failed, please try again",
                            status: "Error"
                        };
                    }
                },
                error: (err) => {
                    this.errorMessage = err
                    this.response = {
                        message: "Email sending failed, please try again",
                        status: "Error"
                    };
                },
            });
        }
        else {
            if (!this.emailForm.controls["emailTo"]?.valid) {
                this.response = {
                    message: "To Address cannot be empty.",
                    status: "Error"
                };
            }
            if (!this.emailForm.controls["emailCc"]?.valid) {
                this.response = {
                    message: "CC Address cannot be empty.",
                    status: "Error"
                };
            }
            if (!this.emailForm.controls["emailSubject"]?.valid) {
                this.response = {
                    message: "Please enter the subject",
                    status: "Error"
                };
            }
            if (!this.emailForm.controls["emailQuestion"]?.valid) {
                this.response = {
                    message: "Please enter the question.",
                    status: "Error"
                };
            }
            if (!this.emailForm.controls["emailClosing"]?.valid) {
                this.response = {
                    message: "Please enter the closing note.",
                    status: "Error"
                };
            }
        }
    }

    addToCCAddress() {
        const existingSelectedUsers = this.emailForm.controls["emailCc"]?.value || "";
        const selectedUsers = this.users.filter(user => user.isSelected);

        const emailArray = [
            ...existingSelectedUsers.split(";").filter(email => email),
            ...selectedUsers.map(user => user.userEmail)
        ];

        const uniqueEmailArray = Array.from(new Set(emailArray));

        this.emailForm.patchValue({ emailCc: uniqueEmailArray.join(";") });

        selectedUsers.forEach(user => user.isSelected = false);
    }

    addToToAddress() {
        const existingSelectedUsers = this.emailForm.controls["emailTo"]?.value || "";
        const selectedUsers = this.users.filter(user => user.isSelected);

        const emailArray = [
            ...existingSelectedUsers.split(";").filter(email => email),
            ...selectedUsers.map(user => user.userEmail)
        ];

        const uniqueEmailArray = Array.from(new Set(emailArray));

        this.emailForm.patchValue({ emailTo: uniqueEmailArray.join(";") });

        selectedUsers.forEach(user => user.isSelected = false);
    }

    clearToAddress() {
        this.emailForm.patchValue({ emailTo: "" });
    }

    clearCCAddress() {
        this.emailForm.patchValue({ emailCc: "" });
    }

    convertDateString(dateInput: string): string {
        const [year, month, day] = dateInput.split('-');
        return `${month}/${day}/${year}`;
    }

    getNextImmediateSunday(dateInput: string) {
        //const [year, month, day] = dateInput.split('/').map(Number);
        const [month, day, year] = dateInput.split('/').map(Number);
        const date = new Date(year, month - 1, day);

        const dayOfWeek = date.getDay();
        const daysUntilNextImmediateSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;

        const nextSunday = new Date(date);
        nextSunday.setDate(date.getDate() + daysUntilNextImmediateSunday);

        const nextSundayString = `${(nextSunday.getMonth() + 1).toString().padStart(2, '0')}/${nextSunday.getDate().toString().padStart(2, '0')}/${nextSunday.getFullYear()}`;
        return nextSundayString;
    }
}


